import React, { useState, useCallback, useEffect } from 'react';

// Composant de notification corrigé
const ModernNotification = ({
                                id,
                                type = 'info',
                                message,
                                title,
                                duration = 5000,
                                autoClose = true,
                                onClose
                            }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [progress, setProgress] = useState(100);

    const handleClose = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => {
            onClose && onClose(id);
        }, 300);
    }, [id, onClose]);

    useEffect(() => {
        // Animation d'entrée
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 50);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (autoClose && duration > 0) {
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    const newProgress = prev - (100 / (duration / 100));
                    return newProgress <= 0 ? 0 : newProgress;
                });
            }, 100);

            const closeTimer = setTimeout(() => {
                handleClose();
            }, duration);

            return () => {
                clearInterval(progressInterval);
                clearTimeout(closeTimer);
            };
        }
    }, [duration, autoClose, handleClose]);

    const getNotificationConfig = (type) => {
        const configs = {
            success: {
                icon: 'ri-checkbox-circle-fill',
                bgColor: 'bg-[#264D3F]',
                borderColor: 'border-[#264D3F]',
                textColor: 'text-gray-800',
                titleColor: 'text-[#264D3F]',
                progressColor: 'bg-[#264D3F]',
                shadowColor: 'shadow-[#264D3F]/10'
            },
            error: {
                icon: 'ri-close-circle-fill',
                bgColor: 'bg-red-500',
                borderColor: 'border-red-100',
                textColor: 'text-gray-800',
                titleColor: 'text-red-600',
                progressColor: 'bg-red-500',
                shadowColor: 'shadow-red-500/10'
            },
            warning: {
                icon: 'ri-alert-fill',
                bgColor: 'bg-[#FFD700]',
                borderColor: 'border-[#FFD700]',
                textColor: 'text-gray-800',
                titleColor: 'text-gray-900',
                progressColor: 'bg-[#FFD700]',
                shadowColor: 'shadow-[#FFD700]/10'
            },
            info: {
                icon: 'ri-information-fill',
                bgColor: 'bg-[#264D3F]',
                borderColor: 'border-[#264D3F]',
                textColor: 'text-gray-800',
                titleColor: 'text-[#264D3F]',
                progressColor: 'bg-[#264D3F]',
                shadowColor: 'shadow-[#264D3F]/10'
            },
            love: {
                icon: 'ri-heart-fill',
                bgColor: 'bg-[#264D3F]',
                borderColor: 'border-[#264D3F]',
                textColor: 'text-gray-800',
                titleColor: 'text-[#264D3F]',
                progressColor: 'bg-[#264D3F]',
                shadowColor: 'shadow-[#264D3F]/10'
            },
            premium: {
                icon: 'ri-vip-crown-fill',
                bgColor: 'bg-gradient-to-r from-[#264D3F] to-[#FFD700]',
                borderColor: 'border-[#FFD700]',
                textColor: 'text-gray-800',
                titleColor: 'text-[#264D3F]',
                progressColor: 'bg-gradient-to-r from-[#264D3F] to-[#FFD700]',
                shadowColor: 'shadow-[#FFD700]/10'
            }
        };

        return configs[type] || configs.info;
    };

    const config = getNotificationConfig(type);

    return (
        <div
            className={`
                w-96 max-w-[calc(100vw-2rem)] mb-3
                transform transition-all duration-300 ease-out
                ${isVisible && !isExiting
                ? 'translate-x-0 opacity-100 scale-100'
                : 'translate-x-full opacity-0 scale-95'
            }
            `}
        >
            <div
                className={`
                    relative overflow-hidden rounded-xl border-2 backdrop-blur-sm
                    bg-white shadow-lg ${config.borderColor} ${config.shadowColor}
                    hover:shadow-xl transition-all duration-300
                `}
            >
                {/* Barre de progression */}
                {autoClose && duration > 0 && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-gray-200">
                        <div
                            className={`h-full transition-all duration-100 ${config.progressColor}`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                )}

                {/* Contenu principal */}
                <div className="p-4">
                    <div className="flex items-start gap-3">
                        {/* Icône */}
                        <div className={`
                            flex-shrink-0 w-10 h-10 rounded-lg ${config.bgColor} 
                            flex items-center justify-center shadow-md
                        `}>
                            <i className={`${config.icon} text-xl text-white`} />
                        </div>

                        {/* Contenu textuel */}
                        <div className="flex-1 min-w-0">
                            {title && (
                                <h4 className={`
                                    text-base font-semibold mb-1 ${config.titleColor}
                                    flex items-center gap-2
                                `}>
                                    {title}
                                    {type === 'premium' && (
                                        <i className="ri-sparkling-2-fill text-yellow-500 text-xs" />
                                    )}
                                </h4>
                            )}

                            <p className={`
                                text-sm ${config.textColor}
                                ${!title ? 'font-medium' : ''}
                            `}>
                                {message}
                            </p>
                        </div>

                        {/* Bouton de fermeture */}
                        <button
                            onClick={handleClose}
                            className={`
                                flex-shrink-0 w-6 h-6 rounded-md 
                                flex items-center justify-center
                                hover:bg-gray-100 transition-colors duration-200
                                ${config.textColor} hover:opacity-80
                            `}
                            aria-label="Fermer la notification"
                        >
                            <i className="ri-close-line text-lg" />
                        </button>
                    </div>
                </div>

                {/* Effet premium */}
                {type === 'premium' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />
                )}
            </div>
        </div>
    );
};
export default ModernNotification;